import { useQuery } from '@tanstack/react-query';
import { Package } from '../models';
import QUERY_KEYS from '../utils/QueryKeys';

/**
 * The GIFT-category pricelist for a school — the package a standalone gifts
 * order is booked against, and the source of its $0 option.
 *
 * ⚠️ `schoolId` IS PART OF THE CACHE KEY, and leaving it out was a real bug.
 * The key was `[PRICELIST, holiday]` for every school, so one cache entry
 * answered for all of them and WHICH answer you got depended on when the
 * customer resolved:
 *
 *   cold load  — the customer is not restored yet, so this ran with schoolId
 *                undefined, fetched the gift pricelist UNFILTERED, and cached
 *                it. The later run with a real school id hit the same key and
 *                never refetched, so a school with no gift pricelist still
 *                appeared to have one.
 *   reload     — the customer is already in localStorage, so the FIRST run had
 *                the school id, the filtered query came back empty, and the
 *                empty result was cached.
 *
 * The visible symptom was an add-ons page that rendered on a first visit and
 * was BLANK on every refresh (HolidayBuilder used to `return null` on a missing
 * pricelist). The invisible one is worse: a customer who switched schools
 * mid-session kept the first school's gift package, and `orderPackage.packageId`
 * then pointed at a package their school is not attached to.
 *
 * ⚠️ THE FALLBACK IS DELIBERATE, and it stays inside the GIFT category. Most
 * schools carry the gift pricelist; the ones that do not must still be able to
 * buy add-ons, and the unfiltered gift package is what production has in fact
 * been serving them all along — by way of the race above. Making it explicit
 * changes no behaviour, it just stops the answer depending on timing.
 *
 * It must NOT fall back to the browsed pricelist (`?pid=`). That can be a
 * senior-portraits pricelist, and pairing its option with a gift package is the
 * misattribution HolidayBuilder's mutation comment describes.
 */
export default function useHolidayPricelist(schoolId) {
  return useQuery(
    [QUERY_KEYS.PRICELIST, QUERY_KEYS.holiday, schoolId ?? null],
    async () => {
      const giftPricelist = () => Package
        .includes([{ options: ['prices'] }])
        .where({ category: 'gift' });

      if (schoolId) {
        const forSchool = await giftPricelist()
          .where({ schoolId })
          .first()
          .then((res) => res.data);

        if (forSchool) return forSchool;

        // Loud in dev only: a school with no gift pricelist is a data gap
        // someone should close on /admin, not a state to discover from a
        // support ticket. Silent in production -- the fallback is correct, and
        // this would log on every page view for every affected school.
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(
            `[gifts] School ${schoolId} has no gift-category pricelist; `
            + 'falling back to the unfiltered one. Attach it on /admin.',
          );
        }
      }

      return giftPricelist().first().then((res) => res.data) || null;
    },
    // The customer always has a school on this page. Fetching before it
    // resolves is what cached a school-less answer in the first place.
    { enabled: !!schoolId },
  );
}
